import type { ForwardedRef, ReactNode, Ref } from 'react';
import { forwardRef, useRef } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import type { Theme } from '@hivebrite/design-system/Theme';
import { useTheme, useBreakpoint } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { RenderSectionContent } from 'hb-builder/components/BuilderComponents/common/Section/components/RenderSectionContent';
import { useIntersectionObserver } from 'hb-builder/components/BuilderComponents/common/Section/hooks/useIntersectionObserver';
import { sectionImageRecommendedSize } from 'hb-builder/components/BuilderComponents/constants/imageSizes';
import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import Drop from 'hb-builder/components/common/Drop';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import {
  BACKGROUND_ARRANGEMENT,
  BACKGROUND_POSITION,
  BACKGROUND_REPEAT,
} from 'hb-builder/constants/imageArrangement';
import { SectionProvider } from 'hb-builder/contexts';
import {
  useBuilderContext,
  useComponentContext,
  useBuilderConfigContext,
  useLayoutContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import type { WorkspaceConfig } from 'hb-react/backoffice/components/PageBuilder/types/workspaceConfig';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { BodyItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/bodyItemAttributes';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';

import CustomizableSection, {
  type CustomizableSectionProps,
} from './components/CustomizableSection';
import DSSection from './components/DSSection';
import WidthContentBox from './components/WidthContentBox';
import SectionSettings from './SectionSettings';
import type {
  WrapperInnerSectionStyleProps,
  WrapperInnerStyleProps,
} from './style';
import {
  getInnerStyle,
  getInnerStyleWithDynamicComponent,
  getWrapperInnerSectionBg,
  getWrapperInnerSectionStyle,
  getWrapperSectionStyle,
  wrapperInnerStyle,
} from './style';

const componentType = 'section';

const Section: BuilderComponent<SectionItemAttributes> = () => <div />; // never used

interface SectionContentProps
  extends Omit<GridSystemWrapperProps, 'isSmallScreen'> {
  forceContentOutsideGrid?: boolean;
  item: DocumentItem;
  sectionRef: Ref<HTMLDivElement>;
  workspaceConfig: WorkspaceConfig;
}

const SectionContent = forwardRef(
  (
    {
      attributes,
      bodyWidth,
      children,
      componentTheme,
      forceContentOutsideGrid,
      isMobile,
      isResizing,
      item,
      sectionRef,
      workspaceConfig,
    }: SectionContentProps,
    dropRef,
  ) => {
    const { overSectionDirection } = useBuilderContext();
    const theme = useTheme();

    // Add margin, when dragging a single component at the top or bottom of a section
    // in order to display the placeholder
    const getOverSectionStyle = () => {
      if (!overSectionDirection?.[item.id]) {
        return {
          marginTop: 0,
          marginBottom: 0,
        };
      }

      return {
        [overSectionDirection[item.id]]: '3px',
      };
    };

    return (
      <BuilderThemeProvider viewAs={VIEW_AS.ADMIN}>
        {/* @ts-expect-error not typed */}
        <WrapperBuilder
          attributes={{}}
          dataTestId="builder-component-section"
          ref={dropRef}
          style={{
            ...getWrapperSectionStyle({
              attributes,
              isMobile,
              colors: theme.colors,
            }),
            ...getOverSectionStyle(),
          }}
        >
          {({ isDragging }) =>
            isDragging ? null : (
              <WidthContentBox
                width={!forceContentOutsideGrid ? bodyWidth : undefined}
                workspaceConfig={workspaceConfig}
                attributes={attributes}
              >
                <GridSystemWrapper
                  attributes={attributes}
                  bodyWidth={bodyWidth}
                  isMobile={isMobile}
                  isResizing={isResizing}
                  isSmallScreen={workspaceConfig.isSmallScreen || false}
                  ref={sectionRef}
                  componentTheme={componentTheme}
                >
                  {children}
                </GridSystemWrapper>
              </WidthContentBox>
            )
          }
        </WrapperBuilder>
      </BuilderThemeProvider>
    );
  },
);

const RenderBuilder = ({
  attributes: explicitAttributes,
  children,
}: {
  attributes: SectionItemAttributes;
  children: ReactNode;
}) => {
  const { getBodyItem, isResizing } = useBuilderContext();
  const { item } = useComponentContext();
  const {
    componentTheme,
    features: { workspace: workspaceConfig },
  } = useBuilderConfigContext();
  const { centerWidth, isMobile } = useLayoutContext();

  const attributes = { ...item?.attributes, ...explicitAttributes };

  const { bodyWidth } = (getBodyItem()?.attributes as BodyItemAttributes) || {};

  const forceContentOutsideGrid = attributes.outsideGrid;

  return (
    <>
      <SectionProvider
        bodyWidth={bodyWidth}
        centerWidth={centerWidth}
        item={item}
      >
        {({ ref: sectionRef }) => (
          <Drop accept={['section', 'structure', 'single']}>
            {({ ref }) => (
              <BuilderThemeProvider viewAs={VIEW_AS.USER}>
                <StyledWrapper>
                  <SectionContent
                    attributes={attributes}
                    bodyWidth={bodyWidth}
                    componentTheme={componentTheme}
                    forceContentOutsideGrid={forceContentOutsideGrid}
                    isMobile={isMobile}
                    isResizing={isResizing}
                    item={item}
                    ref={ref}
                    sectionRef={sectionRef}
                    workspaceConfig={workspaceConfig}
                  >
                    {children}
                  </SectionContent>
                </StyledWrapper>
              </BuilderThemeProvider>
            )}
          </Drop>
        )}
      </SectionProvider>
      <ImageSetter
        attributeName="containerBackgroundImage"
        item={item}
        imageSize={sectionImageRecommendedSize}
      />
    </>
  );
};

interface GridSystemWrapperProps
  extends Omit<WrapperInnerSectionStyleProps, 'componentType'> {
  bodyWidth: BodyItemAttributes['bodyWidth'] | string;
  children: ReactNode;
  componentTheme: Theme;
  isResizing: CustomizableSectionProps['isResizing'];
}

const GridSystemWrapper = forwardRef(
  (
    {
      attributes,
      bodyWidth,
      children,
      isMobile,
      isResizing,
      isSmallScreen,
      componentTheme,
    }: GridSystemWrapperProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const isGridSection =
      attributes.hasColGap &&
      attributes.dynamicAttributes &&
      attributes.dynamicAttributes?.type !== 'empty';

    if (isGridSection) {
      return (
        <DSSection
          attributes={attributes}
          bodyWidth={bodyWidth}
          gridStyle={getWrapperInnerSectionStyle({
            attributes,
            componentType,
            isMobile,
            isSmallScreen,
          })}
          ref={ref}
          wrapperStyle={getWrapperInnerSectionBg({
            attributes,
            colors: componentTheme.colors,
          })}
        >
          {children}
        </DSSection>
      );
    }

    return (
      <CustomizableSection
        attributes={attributes}
        isMobile={isMobile}
        isResizing={isResizing}
        ref={ref}
        rowStyle={getInnerStyle({
          attributes,
          colors: componentTheme.colors,
          componentType,
          isMobile,
          isSmallScreen,
        })}
      >
        {children}
      </CustomizableSection>
    );
  },
);

const Render = ({
  children,
  item = { id: '', type: '' },
}: RenderProps<DocumentItem<SectionItemAttributes>>) => {
  const { attributes } = item;
  const breakpoint = useBreakpoint();
  const isMobileDevice = useIsMobileDevice();
  const { bodyWidth, isMobilePreview, workspaceConfig } = useRendererContext();
  const theme = useTheme();
  const sectionRef = useRef<HTMLDivElement>(null);

  const forceContentOutsideGrid = attributes?.outsideGrid;

  useIntersectionObserver(sectionRef, item?.id);

  const isMobile =
    isMobilePreview ||
    isMobileDevice ||
    breakpoint === 's' ||
    breakpoint === 'm';

  const isGridSection =
    attributes?.hasColGap &&
    attributes?.dynamicAttributes &&
    attributes?.dynamicAttributes.type !== 'empty';

  return (
    <SectionProvider item={item}>
      {({ ref }) => (
        // @ts-expect-error not typed
        <Wrapper
          isMobilePreview={isMobilePreview}
          ref={sectionRef}
          style={getWrapperSectionStyle({
            attributes,
            isMobile,
            colors: theme?.colors,
          })}
        >
          {attributes?.fullWidth &&
            attributes?.containerBackgroundImage &&
            attributes?.containerBackgroundImageAlt && (
              <span
                role="img"
                aria-label={attributes?.containerBackgroundImageAlt}
              />
            )}
          <WidthContentBox
            attributes={attributes}
            workspaceConfig={workspaceConfig}
            // avoid horizontal scrolling on mobile
            {...(isMobile && { overflow: 'hidden' })}
            {...(!forceContentOutsideGrid && { width: bodyWidth })}
          >
            <WrapperInner
              ref={ref}
              $isMobile={isMobile}
              $minHeight={attributes?.height}
              $mobileDisplay={attributes?.mobileDisplay}
              $verticalAlign={attributes?.verticalAlign}
              data-testid="builder-component-section"
              style={{
                ...(isGridSection
                  ? getInnerStyleWithDynamicComponent({ bodyWidth })
                  : getInnerStyle({
                      attributes,
                      colors: theme?.colors,
                      componentType,
                      isMobile,
                      isSmallScreen: workspaceConfig.isSmallScreen,
                    })),
              }}
            >
              {!isGridSection &&
                !attributes?.fullWidth &&
                attributes?.containerBackgroundImage &&
                attributes?.containerBackgroundImageAlt && (
                  <span
                    role="img"
                    aria-label={attributes.containerBackgroundImageAlt}
                  />
                )}
              <RenderSectionContent
                attributes={item?.attributes}
                isGridSection={isGridSection}
                isMobile={isMobile}
                ref={ref}
              >
                {children}
              </RenderSectionContent>
            </WrapperInner>
          </WidthContentBox>
        </Wrapper>
      )}
    </SectionProvider>
  );
};

Section.componentName = 'Section';
Section.componentLabel = __('Section');
Section.componentType = componentType;
Section.componentCategory = 'simple';

Section.canBeDoubleClicked = true;
Section.canBeDragged = true;
Section.canBeListed = false;
Section.canBeSelected = true;

const defaultConfig = () => ({
  backgroundType: 'image',
  category: Section.componentCategory,
  containerBackgroundPosition: BACKGROUND_POSITION.CENTER,
  containerBackgroundRepeat: BACKGROUND_REPEAT.NO_REPEAT,
  containerBackgroundSize: BACKGROUND_ARRANGEMENT.COVER,
  flexDirection: 'row',
  mobileDisplay: 'stack',
  name: Section.componentName,
  type: Section.componentName,
  verticalAlign: 'top',
});

Section.item = ({ children = [], features }) => {
  const sectionConfig = {
    ...defaultConfig(),
    ...(features?.section || {}),
  };

  return {
    attributes: {
      backgroundType: sectionConfig.backgroundType,
      containerBackgroundColor: sectionConfig.containerBackgroundColor,
      containerBackgroundImage: sectionConfig.containerBackgroundImage,
      containerBackgroundImageAlt: sectionConfig.containerBackgroundImageAlt,
      containerBackgroundPosition: sectionConfig.containerBackgroundPosition,
      containerBackgroundRepeat: sectionConfig.containerBackgroundRepeat,
      containerBackgroundSize: sectionConfig.containerBackgroundSize,
      containerBoxShadow: sectionConfig.containerBoxShadow,
      containerPadding: sectionConfig.containerPadding,
      dynamicAttributes: sectionConfig.dynamicAttributes,
      flexDirection: sectionConfig.flexDirection,
      fullWidth: sectionConfig.fullWidth,
      hasColGap: sectionConfig.hasColGap,
      height: sectionConfig.height,
      horizontalAlign: sectionConfig.horizontalAlign,
      keepContainerPaddingScreenS: sectionConfig.keepContainerPaddingScreenS,
      mobileDisplay: sectionConfig.mobileDisplay,
      numCols: sectionConfig.numCols,
      outsideGrid: sectionConfig.outsideGrid,
      sectionWidth: sectionConfig.sectionWidth,
      themeOverridden: sectionConfig.themeOverridden,
      verticalAlign: sectionConfig.verticalAlign,
    },
    category: sectionConfig.category,
    children,
    name: sectionConfig.name,
    shouldBeLazyLoaded: sectionConfig?.shouldBeLazyLoaded,
    type: sectionConfig.type,
  };
};

Section.toolbar = ['edit', 'drag', 'columns-layout', 'duplicate', 'delete'];

Section.Settings = SectionSettings;
Section.SettingsTabs = [];

Section.RenderBuilder = RenderBuilder;
Section.Render = Render;

export default Section;

export const WrapperInner = styled(Box)<WrapperInnerStyleProps>`
  ${wrapperInnerStyle}
`;

const StyledWrapper = styled(Box).attrs(() => ({
  display: 'block',
  height: '100%',
  width: '100%',
}))``;
